import { apiClient, unwrap } from './client';

/**
 * Coûts de main-d'œuvre — 29/09/2026.
 *
 * Les montants circulent en chaînes de centimes : un arrondi de virgule
 * flottante sur un tarif qui décide d'une paie n'a aucune excuse.
 */

/** Ferronnerie (soudeurs) ajoutée le 26/10/2026 ; facultative pour un produit (absente = 0). */
export type CategorieMainOeuvre = 'armature' | 'ferronnerie' | 'production';

export const LIBELLE_CATEGORIE: Record<CategorieMainOeuvre, string> = {
  armature:    'Armature',
  ferronnerie: 'Ferronnerie',
  production:  'Production',
};

export interface TarifCategorie {
  id:           string;
  libelle:      string;
  montantCents: string;
}

export interface TarifProduit {
  produitId:   string;
  code:        string;
  designation: string;
  famille:     string;
  uniteVente:  string;
  categories:  Record<CategorieMainOeuvre, TarifCategorie | null>;
  autres:      { id: string; libelle: string; categorie: string | null; montantCents: string }[];
  /** Coût de main-d'œuvre d'une unité, toutes équipes confondues. */
  totalCents:  string;
  /** Vrai quand les deux catégories sont tarifées. */
  complet:     boolean;
}

export interface TauxJournaliers {
  tauxDefautCents: string | null;
  ouvriers: {
    id: string; nom: string; telephone: string; metier: string | null;
    tauxCents: string | null;
    /** Ce qui lui sera réellement appliqué : le sien, sinon celui de la filiale. */
    tauxApplique: string | null;
  }[];
}

/**
 * Confrontation des barèmes au catalogue : poteaux (2 500 FCFA à 9 m, +500 par
 * mètre) et caniveaux (1 500 FCFA pour un 60 × 60 × 200, +250 par tranche de
 * 10 cm). Rien n'est écrit tant que `appliquerBaremes` n'est pas appelé.
 */
export interface SimulationBaremes {
  baremes: {
    poteaux:   { longueurPivot: number; montantPivotFcfa: number; pasParMetreFcfa: number };
    caniveaux: {
      referenceLargeurCm: number; referenceHauteurCm: number; longueurElementCm: number;
      montantReferenceFcfa: number; pasFcfa: number; tranchehCm: number;
    };
  };
  lignes: {
    produitId: string;
    code: string;
    bareme: 'poteaux' | 'caniveaux';
    /** Ce qui identifie la taille pour un humain : « 12 m », « 60 × 60 × 200 ». */
    taille: string;
    tarifFcfa: number;
    /** Hors de la plage dictée : sous 9 m, ou section sous 60 × 60. Une déduction. */
    extrapole: boolean;
    changements: {
      categorie: CategorieMainOeuvre;
      avantFcfa: number | null;
      apresFcfa: number;
      effet: 'creation' | 'hausse' | 'baisse' | 'inchange';
    }[];
  }[];
  /** Produits qu'aucun barème ne couvre, avec le motif — jamais un silence. */
  ignores: { code: string; raison: string }[];
  compte: { creations: number; hausses: number; baisses: number; inchanges: number };
}

export interface Equipe {
  id:                  string;
  code:                string;
  nom:                 string;
  chefNom:             string | null;
  specialite:          string | null;
  categorieMainOeuvre: CategorieMainOeuvre | null;
  membres: {
    id: string; membreId: string; nom: string; telephone: string;
    metier: string | null; photoUrl: string | null; parts: string;
  }[];
}

export const coutsApi = {
  tarifs: () =>
    apiClient.get('/couts-main-oeuvre/tarifs').then(unwrap<TarifProduit[]>),

  definirTarif: (produitId: string, body: { categorie: CategorieMainOeuvre; montantFcfa: number; libelle?: string }) =>
    apiClient.put(`/couts-main-oeuvre/tarifs/${produitId}`, body).then(unwrap<{ id: string }>),

  retirerTarif: (id: string) =>
    apiClient.delete(`/couts-main-oeuvre/tarifs/${id}`).then(unwrap<{ id: string }>),

  tauxJournaliers: () =>
    apiClient.get('/couts-main-oeuvre/taux-journaliers').then(unwrap<TauxJournaliers>),

  definirTauxDefaut: (montantFcfa: number | null) =>
    apiClient.patch('/couts-main-oeuvre/taux-journaliers/defaut', { montantFcfa }).then(unwrap<unknown>),

  definirTauxOuvrier: (ouvrierId: string, montantFcfa: number | null) =>
    apiClient.patch(`/couts-main-oeuvre/taux-journaliers/${ouvrierId}`, { montantFcfa }).then(unwrap<unknown>),

  simulerBaremes: () =>
    apiClient.get('/couts-main-oeuvre/baremes').then(unwrap<SimulationBaremes>),

  appliquerBaremes: (inclureExtrapoles: boolean) =>
    apiClient.post('/couts-main-oeuvre/baremes', { inclureExtrapoles }).then(unwrap<{
      ecrits: number;
      produitsTraites: number;
      extrapolesEcartes: { code: string; taille: string; tarifFcfa: number }[];
    }>),

  equipes: () =>
    apiClient.get('/couts-main-oeuvre/equipes').then(unwrap<Equipe[]>),

  creerEquipe: (body: { code: string; nom: string; categorieMainOeuvre: CategorieMainOeuvre; chefNom?: string }) =>
    apiClient.post('/couts-main-oeuvre/equipes', body).then(unwrap<Equipe>),

  definirMembres: (equipeId: string, membres: { ouvrierId: string; parts?: number }[]) =>
    apiClient.put(`/couts-main-oeuvre/equipes/${equipeId}/membres`, { membres }).then(unwrap<Equipe>),
};
